import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import FACEBOOK from '@/assets/images/account/facebook.svg';
import GOOGLE from '@/assets/images/account/google.svg';
import WECHAT from '@/assets/images/account/wechat.svg';

import Constants from '@/constants/const';
import { facebookLogin } from '@/api/customer';
import { Toast } from '@/components/common/CommonToast';
import { WeeeIcon } from '@/components/common/Icon';
import { hideLoading, showLoading } from '@/components/common/Indicator/pageIndicator';
import useClickActionT2 from '@/tracks/useClickActionT2';
import { getInviterParams, getReferralIdFromCookie } from '@/utils/common/referral';
import EventEmitter, { actions } from '@/utils/eventemitter';
import { loadFacebookSdk } from '@/utils/facebook';
import logger from '@/utils/logger';

import AccountDeletedContent from '@/components/account/AccountDeletedContent';
import GoogleOauthLogin from '@/components/common/GoogleOauthLogin';
import Popup from '@/components/common/Popup';
import { LoginMethod } from '@/types/account.d';
import {
  isFacebookBrowser,
  isFBBrowser,
  isInstagramBrowser,
  isMobileBrowser,
  isTiktokBrowser,
  isWeixinBrowser
} from '@/utils/common/device';
import {
  afterUserLogin,
  afterUserLoginToOnboarding,
  afterUserRegisterLogin,
  afterUserSign
} from '@/utils/common/pixel';
import { getUniqueId } from '@/utils/common/uniqueId';
import { addParameterToUrl, buildUrlWithOrigin } from '@/utils/url';
import { twMerge } from 'tailwind-merge';
import { isLatinoChannel } from '@/utils/common/latino-app';
import { getSafeRedirectUrl } from '@/utils/safeRedirect';

type ThirdLoginProps = {
  className?: string;
  trackType?: string;
  handleClickTrack?: (trackType: string, clickType: string) => void;
  clickTrack?: (type: string) => void;
  onTabClick?: (tab: number) => void;
  /**登录注册弹框成功回调 */
  onThirdLoginSuccess?: (arg?: any, res?: any) => void;
  currentTab?: number;
  isLoginSignupPopup?: boolean;
  wechatPopUpSuccessUrl?: string;
  redirect?: string;
  showNewStyle?: boolean;
  size?: 'small' | 'medium';
  tipsText?: string | React.ReactNode;
};

const ChannelWidget = props => {
  const { className, handleClick, children, ...rest } = props;
  return (
    <div
      data-testid="wid-third-login-item"
      className={twMerge(
        classNames(
          'w-[43px] h-[43px] p-[10px] rounded-full inline-flex justify-center items-center last-of-type:mr-0',
          className,
          'needsclick'
        )
      )}
      onClick={handleClick}
      {...rest}>
      {children}
    </div>
  );
};

const ThirdLogin = (props: ThirdLoginProps) => {
  const {
    handleClickTrack,
    trackType,
    onTabClick,
    currentTab,
    clickTrack,
    onThirdLoginSuccess,
    wechatPopUpSuccessUrl,
    isLoginSignupPopup,
    showNewStyle = true,
    tipsText,
    size = 'small',
    className
  } = props;

  const isSmall = size === 'small';

  const [showWXLogin, setShowWXLogin] = useState(false);
  const [showFbLogin, setShowFbLogin] = useState(false);
  //fackbook 授权登录, 在facebook app中会使用授权的方式去登录
  const [showFBAuthLogin, setShowFBAuthLogin] = useState(false);
  const [showDeletedPopup, setShowDeletedPopup] = useState<boolean>(false);
  const isTiktok = isTiktokBrowser();
  const isPc = !isMobileBrowser();
  const [showGoogleOauthLogin, setShowGoogleOauthLogin] = useState(false);

  const router = useRouter();
  const { t: tCommon, lang } = useTranslation('common');
  const { buttonClick } = useClickActionT2();
  const isLatino = isLatinoChannel();
  const isTh = lang === Constants.LANGUAGE_TH;

  useEffect(() => {
    setShowWXLogin(!isTh && (isWeixinBrowser() || isPc));
    const showFbAuth = isFacebookBrowser();
    setShowFBAuthLogin(showFbAuth);
    setShowFbLogin(!isWeixinBrowser() && !isTiktok && !showFbAuth);
    setShowGoogleOauthLogin(!isWeixinBrowser());
  }, [isTiktok, isPc, isLatino, isTh]);

  const redirectUrl = props?.redirect ? props.redirect : getSafeRedirectUrl(router.query?.redirect_url as string);
  const forceMethod = router.query?.force_method as string;

  useEffect(() => {
    if (showFbLogin) {
      loadFacebookSdk().then(res => {
        if (res && forceMethod === LoginMethod.FACEBOOK) {
          handleFbLogin(res);
        }
      });
      return;
    }
    if (showFBAuthLogin && forceMethod === LoginMethod.FACEBOOK) {
      handleFbAuthLogin();
      return;
    }
  }, [showFbLogin, showFBAuthLogin, forceMethod]);

  const onSuccess = (isLoginSignUpPopUp = false, lang, redicrectUrl, isBind, isPhoneABTestBind, thirdLoginType) => {
    // fb 登录验证的弹框关闭后 wechat跳到邮箱绑定页，fb是展示bind-connect弹框，把bind-connect和siggnup写成一个弹框
    if (!isPhoneABTestBind) {
      if (isLoginSignUpPopUp) {
        EventEmitter.emit(actions.showPhoneConnectPopUp);
        onThirdLoginSuccess?.();
        return;
      }
      const reirectUrl = `${
        process.env.NEXT_PUBLIC_WEB_HOST
      }/${lang}/account/phone-connect?channel_type=phone_verify&redirect_url=${encodeURIComponent(
        redicrectUrl
      )}&type=${thirdLoginType}`;
      window.location.href = reirectUrl;
      return;
    }
    if (isBind) {
      if (isLoginSignUpPopUp) {
        EventEmitter.emit(actions.showBindConnetPopUp);
        onThirdLoginSuccess?.();
        return;
      }
      const reirectUrl = `${
        process.env.NEXT_PUBLIC_WEB_HOST
      }/${lang}/account/bind-connect?redirect_url=${encodeURIComponent(redicrectUrl)}&type=${thirdLoginType}`;
      window.location.href = reirectUrl;
      return;
    }
    if (isLoginSignUpPopUp) {
      onThirdLoginSuccess?.();
      router.reload();
      return;
    }
    onThirdLoginSuccess?.({}, { is_binded: isPhoneABTestBind, is_need_bind: isBind });
  };

  const handleGoogleClick = () => {
    handleClickTrack?.(trackType, 'google');
    if (clickTrack) {
      clickTrack?.('gg');
      return;
    }
    buttonClick({
      co: {
        target_nm: 'google'
      }
    });
  };

  const handleWechatLogin = () => {
    handleClickTrack?.(trackType, 'wechat');
    if (clickTrack) {
      clickTrack?.('wx');
    } else {
      buttonClick({
        co: {
          target_nm: 'wechat'
        }
      });
    }

    const redirectLink = wechatPopUpSuccessUrl || (redirectUrl as string);
    window.location.href = buildUrlWithOrigin(
      `/${lang}/account/login/wechat?redirect_url=${encodeURIComponent(
        isLoginSignupPopup ? addParameterToUrl(redirectLink, 'isLoginSignupPopup', '1') : redirectLink
      )}`
    );
  };

  const handleFbLogin = async (res?) => {
    handleClickTrack?.(trackType, 'facebook');
    if (clickTrack) {
      clickTrack?.('fb');
    } else {
      buttonClick({
        co: {
          target_nm: 'facebook'
        }
      });
    }

    try {
      const sdk = !!res ? res : await loadFacebookSdk();
      if (sdk) {
        window.FB.login(
          response => {
            console.log('response----', response);
            if (response.status === 'connected') {
              window.FB.api(
                '/me',
                { fields: 'id, name, first_name, last_name, picture.type(large), email' },
                async userInfo => {
                  try {
                    showLoading();
                    const res = await facebookLogin({
                      user_info: JSON.stringify(userInfo),
                      referral_id: getReferralIdFromCookie(),
                      ...getInviterParams()
                    });
                    hideLoading();
                    if (res?.result) {
                      const isBind = res?.object?.is_need_bind || 0;
                      const isPhoneABTestBind = res?.object?.is_binded ?? true;
                      afterUserRegisterLogin(res?.object?.token, res?.object?.language);
                      if (res?.object?.is_sign_up) {
                        afterUserSign('facebook');
                      } else {
                        afterUserLogin('facebook');
                      }
                      // 用户注册、登录成功之后，优先根据next_url跳转
                      await afterUserLoginToOnboarding(res?.object?.next_url, redirectUrl);

                      onSuccess(isLoginSignupPopup, lang, redirectUrl, isBind, isPhoneABTestBind, 'facebook');
                    } else {
                      Toast({
                        text: res?.message,
                        duration: 3000,
                        showIcon: false
                      });
                    }
                  } catch (err) {
                    console.log('catch2----', err);
                    logger.error({
                      page: window.location.href,
                      method: 'fb login /me',
                      err
                    });
                  }
                }
              );
            }
          },
          {
            scope: 'public_profile,email'
          }
        );
      }
    } catch (err) {
      console.log('catch----', err);
      logger.error({
        page: window.location.href,
        method: handleFbLogin.name,
        err
      });
    }
  };

  const handleFbAuthLogin = () => {
    sessionStorage.setItem('fbAuthLoginSuccessRedirectUrl', redirectUrl as string);
    const redirectUri = `${process.env.NEXT_PUBLIC_WEB_HOST}/en/account/auth/fb`;
    window.location.href = `https://m.facebook.com/v15.0/dialog/oauth?client_id=${
      process.env.NEXT_PUBLIC_FB_CLIENT_ID
    }&redirect_uri=${encodeURIComponent(
      redirectUri
    )}&response_type=code&state=${getUniqueId()}&scope=public_profile,email`;
  };

  const thirdMethods = [
    {
      show: showGoogleOauthLogin,
      iconImg: GOOGLE,
      name: 'Google',
      text: tCommon('continue_with_google'),
      icon: <WeeeIcon type="icongoogle" className="text-reserved-true-white !text-[28px] !leading-none" />,
      iconClass: 'bg-[#6580E0]'
    },
    {
      show: showFbLogin,
      iconImg: FACEBOOK,
      name: 'Facebook',
      onClick: () => handleFbLogin(),
      text: tCommon('continue_with_facebook'),
      icon: <WeeeIcon type="iconfb" className="text-reserved-true-white !text-[30px] !leading-none" />,
      iconClass: 'bg-[#49568d]'
    },
    {
      show: showFBAuthLogin,
      iconImg: FACEBOOK,
      name: 'Facebook',
      onClick: () => handleFbAuthLogin(),
      text: tCommon('continue_with_facebook'),
      icon: <WeeeIcon type="iconfb" className="text-reserved-true-white !text-[30px] !leading-none" />,
      iconClass: 'bg-[#49568d]'
    },
    {
      show: !isLatino && showWXLogin,
      iconImg: WECHAT,
      name: 'Wechat',
      onClick: () => handleWechatLogin(),
      text: tCommon('continue_with_wechat'),
      icon: <WeeeIcon type="iconwechat" className="text-reserved-true-white !text-[34px] !leading-none" />,
      iconClass: 'bg-[#2dc100]'
    }
  ];

  return (
    <>
      {isSmall && !isTiktok && (
        <div data-testid="wid-third-login-tips" className="flex justify-center items-center gap-[10px] py-[10px]">
          {tipsText ? (
            tipsText
          ) : (
            <>
              <span data-testid="wid-third-login-tips-text" className="enki-body-2xs text-surface-100-fg-minor">
                {tCommon('continue_with')}
              </span>
              <span className="h-[1px] bg-surface-200-bg flex-1"></span>
            </>
          )}
        </div>
      )}
      <div
        data-testid="wid-third-login-container"
        data-role="third-login"
        className={twMerge(isSmall && 'mt-[15px] flex', size === 'medium' && 'w-full', className)}>
        {thirdMethods.map(item => {
          if (item.show) {
            if (item.name === 'Google') {
              return (
                <div
                  data-testid="wid-third-login-item"
                  data-key={item.name}
                  key={item.name}
                  className={twMerge(
                    size === 'medium' && 'relative w-[350px] h-[42px] mb-3 cursor-pointer',
                    isSmall && 'relative w-[63px] h-[63px] mr-[15px]'
                  )}>
                  <GoogleOauthLogin
                    className="absolute top-0 left-0 right-0 bottom-0 cursor-pointer z-[1]"
                    redirectUrl={redirectUrl as string}
                    isLoginSignupPopup={isLoginSignupPopup}
                    onClick={handleGoogleClick}>
                    <span className="sr-only">Google OAuth Login</span>
                  </GoogleOauthLogin>
                  {isSmall ? (
                    <ChannelWidget className={item.iconClass}>{item.icon}</ChannelWidget>
                  ) : (
                    <div
                      data-testid="wid-third-login-item-google"
                      className={twMerge(
                        'w-[350px] h-[42px] box-border flex items-center justify-center border-2 border-solid border-surface-100-hairline cursor-pointer rounded-full',
                        'needsclick'
                      )}>
                      <img
                        data-testid="wid-third-login-item-google-icon"
                        src={item.iconImg}
                        alt={item.name}
                        className="absolute top-2 left-3 w-6 h-6"
                      />
                      <span
                        data-testid="wid-third-login-item-google-text"
                        className="enki-body-sm-medium text-primary-5">
                        {item.text}
                      </span>
                    </div>
                  )}
                </div>
              );
            }
            if (isSmall) {
              return (
                <ChannelWidget key={item.name} className={item.iconClass} handleClick={item.onClick}>
                  {item.icon}
                </ChannelWidget>
              );
            }
            return (
              <div
                data-testid="wid-third-login-item"
                data-key={item.name}
                key={item.name}
                className="relative w-[350px] h-[42px] box-border mb-3 flex items-center justify-center border-2 border-solid border-surface-100-hairline cursor-pointer rounded-full"
                onClick={item.onClick}>
                <img
                  data-testid="wid-third-login-item-icon"
                  src={item.iconImg}
                  alt={item.name}
                  className="absolute top-2 left-3 w-6 h-6"
                />
                <span data-testid="wid-third-login-item-text" className="enki-body-sm-medium text-primary-5">
                  {item.text}
                </span>
              </div>
            );
          }
          return null;
        })}
      </div>
      <Popup
        popupId="thirdLoginDeletedPopup"
        show={showDeletedPopup}
        position="center"
        wrapperClassName="w-11/12 rounded-600"
        onClose={() => setShowDeletedPopup(false)}>
        <div className="pb-8">
          <AccountDeletedContent popup onClose={() => setShowDeletedPopup(false)} />
        </div>
      </Popup>
    </>
  );
};

export default ThirdLogin;
